import { computed, ref, type ComputedRef, type Ref } from 'vue'

import type { WorkflowInstance } from '@/types/workflow'

import {
  buildConsistencyAssetImagePrompt,
  buildConsistencyAssetLoadingKey,
  buildConsistencyAssetLookups,
  buildConsistencyAssetsPayload,
  buildConsistencyAssetSelectionOptions,
  buildStoryboardPromptMentionItems,
  collapseConsistencyAssetText,
  CONSISTENCY_ASSET_LABELS,
  CONSISTENCY_ASSET_MATERIAL_TYPES,
  createManualConsistencyAsset,
  findConsistencyAssetConflict,
  normalizeConsistencyText,
  normalizeSceneAssetName,
  syncConsistencyAssetsFromStoryboard as syncStoryboardConsistencyAssets,
} from '@/views/customer/generation-workspace/consistencyAssets'
import { cloneScene } from '@/views/customer/generation-workspace/storyboardSceneDrafts'
import type {
  ConsistencyAssetCollection,
  ConsistencyAssetDraft,
  ConsistencyAssetType,
  PromptMentionOption,
  StoryboardSceneDraft,
  StoryboardWorkspaceState,
} from '@/views/customer/generation-workspace/types'

interface UseConsistencyAssetWorkspaceOptions {
  projectId: number
  storyboardWorkspaceState: ComputedRef<StoryboardWorkspaceState>
  storyboardDraftScenes: Ref<StoryboardSceneDraft[]>
  consistencyAssetTab: Ref<ConsistencyAssetType>
  workflow: Ref<WorkflowInstance | null>
  workspaceBusyState: Ref<{ title: string; description: string; stageLabel: string; notice?: string } | null>
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: { successMessage?: string; silent?: boolean }) => Promise<void>
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  uploadMaterial: (formData: FormData, uploadOptions: { project_id: number; material_type: string; description?: string }) => Promise<{ file_url?: string | null; id?: number }>
  generateConsistencyAssetImageApi: (projectId: number, type: ConsistencyAssetType, assetId: string, payload: { prompt: string }) => Promise<any>
  getActionErrorMessage: (error: any) => string | null
  success: (message: string) => void
  error: (message: string) => void
}

export function useConsistencyAssetWorkspace(options: UseConsistencyAssetWorkspaceOptions) {
  const consistencySaving = ref(false)
  const consistencyDirty = ref(false)
  const consistencyAssets = ref<ConsistencyAssetCollection>({ character: [], scene: [], prop: [] })
  const consistencyAssetUploadInput = ref<HTMLInputElement | null>(null)
  const activeConsistencyAssetUploadTarget = ref<{ type: ConsistencyAssetType; id: string } | null>(null)
  const consistencyAssetImageGenerating = ref<Record<string, boolean>>({})
  const consistencyAssetImageUploading = ref<Record<string, boolean>>({})

  const normalizePersistedAssets = (items: any[], type: ConsistencyAssetType): ConsistencyAssetDraft[] => items.map((item) => ({
    ...item,
    type,
    name: type === 'scene' ? normalizeSceneAssetName(item?.name) : String(item?.name || ''),
    prompt: collapseConsistencyAssetText(item?.prompt, item?.notes),
    notes: '',
  }))

  const persistedConsistencyAssets = computed<ConsistencyAssetCollection>(() => {
    const raw = options.storyboardWorkspaceState.value.consistency_assets || {}
    return {
      character: Array.isArray(raw.characters) ? normalizePersistedAssets(cloneScene(raw.characters), 'character') : [],
      scene: Array.isArray(raw.scenes) ? normalizePersistedAssets(cloneScene(raw.scenes), 'scene') : [],
      prop: Array.isArray(raw.props) ? normalizePersistedAssets(cloneScene(raw.props), 'prop') : [],
    }
  })

  const consistencyAssetStats = computed(() => ([
    {
      key: 'character',
      label: '角色资产',
      total: consistencyAssets.value.character.filter((item) => !item.excluded).length,
      ready: consistencyAssets.value.character.filter((item) => !item.excluded && String(item.image_url || '').trim()).length,
    },
    {
      key: 'scene',
      label: '场景资产',
      total: consistencyAssets.value.scene.filter((item) => !item.excluded).length,
      ready: consistencyAssets.value.scene.filter((item) => !item.excluded && String(item.image_url || '').trim()).length,
    },
    {
      key: 'prop',
      label: '道具资产',
      total: consistencyAssets.value.prop.filter((item) => !item.excluded).length,
      ready: consistencyAssets.value.prop.filter((item) => !item.excluded && String(item.image_url || '').trim()).length,
    },
  ]))

  const activeConsistencyAssets = computed(() => (consistencyAssets.value[options.consistencyAssetTab.value] || []).filter((item) => !item.excluded))
  const consistencyAssetLookups = computed(() => buildConsistencyAssetLookups(consistencyAssets.value))
  const consistencyAssetSelectionOptions = computed(() => buildConsistencyAssetSelectionOptions(consistencyAssets.value))
  const storyboardPromptMentionItems = computed<PromptMentionOption[]>(() => buildStoryboardPromptMentionItems(consistencyAssets.value))
  const selectedStoryboardPromptMentionItems = computed(() => storyboardPromptMentionItems.value)

  function markConsistencyDirty() {
    consistencyDirty.value = true
  }

  function syncConsistencyAssetsFromStoryboard() {
    const nextState = syncStoryboardConsistencyAssets({
      storyboardDraftScenes: options.storyboardDraftScenes.value,
      persistedConsistencyAssets: persistedConsistencyAssets.value,
      currentConsistencyAssets: consistencyAssets.value,
      currentTab: options.consistencyAssetTab.value,
    })
    consistencyAssets.value = nextState.assets
    options.consistencyAssetTab.value = nextState.nextTab
  }

  function validateConsistencyAssets(nextAssets: ConsistencyAssetCollection = consistencyAssets.value) {
    const conflictMessage = findConsistencyAssetConflict(nextAssets)
    if (conflictMessage) {
      options.error(conflictMessage)
      return false
    }
    return true
  }

  async function handleSaveConsistencyAssets(successMessage = '一致性资产已保存') {
    if (!validateConsistencyAssets()) {
      return false
    }

    consistencySaving.value = true
    try {
      const current = options.getStoryboardWorkspaceStatePayload()
      await options.persistStoryboardWorkspaceState({
        ...current,
        consistency_assets: buildConsistencyAssetsPayload(consistencyAssets.value),
      }, { successMessage })
      consistencyDirty.value = false
      return true
    } catch (error: any) {
      options.error(options.getActionErrorMessage(error) || '保存一致性资产失败')
      return false
    } finally {
      consistencySaving.value = false
    }
  }

  async function handleCreatePromptMentionAsset(name: string, sceneNumber?: number) {
    const normalizedName = normalizeConsistencyText(name)
    if (!normalizedName) {
      return false
    }

    const existing = consistencyAssets.value.prop.find((item) => normalizeConsistencyText(item.name) === normalizedName)
    if (existing && !existing.excluded) {
      options.consistencyAssetTab.value = 'prop'
      options.success(`道具资产 ${normalizedName} 已存在，可直接继续引用`)
      return true
    }

    const previousAssets = consistencyAssets.value
    const previousTab = options.consistencyAssetTab.value

    const nextAsset = existing
      ? { ...existing, excluded: false }
      : {
        ...createManualConsistencyAsset('prop'),
        name: normalizedName,
        linked_scene_numbers: sceneNumber ? [sceneNumber] : [],
      }

    consistencyAssets.value = {
      ...consistencyAssets.value,
      prop: existing
        ? consistencyAssets.value.prop.map((item) => item.id === existing.id ? nextAsset : item)
        : [...consistencyAssets.value.prop, nextAsset],
    }
    options.consistencyAssetTab.value = 'prop'
    markConsistencyDirty()

    const saved = await handleSaveConsistencyAssets(`已新增道具资产 ${normalizedName}`)
    if (!saved) {
      consistencyAssets.value = previousAssets
      options.consistencyAssetTab.value = previousTab
      return false
    }

    options.success(`已把 ${normalizedName} 加入官方道具资产，现在可以直接用 @${normalizedName}`)
    return true
  }

  function handleAddConsistencyAsset(type: ConsistencyAssetType) {
    consistencyAssets.value = {
      ...consistencyAssets.value,
      [type]: [...consistencyAssets.value[type], createManualConsistencyAsset(type)],
    }
    options.consistencyAssetTab.value = type
    markConsistencyDirty()
  }

  function updateConsistencyAsset(type: ConsistencyAssetType, assetId: string, updater: (asset: ConsistencyAssetDraft) => ConsistencyAssetDraft) {
    consistencyAssets.value = {
      ...consistencyAssets.value,
      [type]: consistencyAssets.value[type].map((item) => item.id === assetId ? updater(item) : item),
    }
    markConsistencyDirty()
  }

  function handleRemoveConsistencyAsset(type: ConsistencyAssetType, assetId: string) {
    const asset = consistencyAssets.value[type].find((item) => item.id === assetId)
    if (asset && !String(asset.source_key || '').startsWith('manual:')) {
      updateConsistencyAsset(type, assetId, (current) => ({
        ...current,
        excluded: true,
      }))
      return
    }

    consistencyAssets.value = {
      ...consistencyAssets.value,
      [type]: consistencyAssets.value[type].filter((item) => item.id !== assetId),
    }
    markConsistencyDirty()
  }

  function triggerConsistencyAssetUpload(type: ConsistencyAssetType, assetId: string) {
    activeConsistencyAssetUploadTarget.value = { type, id: assetId }
    if (consistencyAssetUploadInput.value) {
      consistencyAssetUploadInput.value.value = ''
      consistencyAssetUploadInput.value.click()
    }
  }

  async function handleConsistencyAssetFileSelected(event: Event) {
    const target = event.target as HTMLInputElement | null
    const file = target?.files?.[0]
    const uploadTarget = activeConsistencyAssetUploadTarget.value
    if (!file || !uploadTarget) {
      return
    }

    const loadingKey = buildConsistencyAssetLoadingKey(uploadTarget.type, uploadTarget.id)
    consistencyAssetImageUploading.value = { ...consistencyAssetImageUploading.value, [loadingKey]: true }
    try {
      const asset = consistencyAssets.value[uploadTarget.type].find((item) => item.id === uploadTarget.id)
      const formData = new FormData()
      formData.append('file', file)
      const material = await options.uploadMaterial(formData, {
        project_id: options.projectId,
        material_type: CONSISTENCY_ASSET_MATERIAL_TYPES[uploadTarget.type],
        description: `consistency-${uploadTarget.type}:${asset?.name || uploadTarget.id}`,
      })
      const imageUrl = String(material.file_url || '').trim()
      if (!imageUrl) {
        throw new Error('上传成功，但未返回图片地址')
      }

      updateConsistencyAsset(uploadTarget.type, uploadTarget.id, (current) => ({
        ...current,
        image_url: imageUrl,
        material_id: material.id,
        selected_image_source: 'uploaded',
      }))
      await handleSaveConsistencyAssets(`${CONSISTENCY_ASSET_LABELS[uploadTarget.type]}参考图已保存`)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '上传参考图失败')
    } finally {
      consistencyAssetImageUploading.value = { ...consistencyAssetImageUploading.value, [loadingKey]: false }
      activeConsistencyAssetUploadTarget.value = null
      if (target) {
        target.value = ''
      }
    }
  }

  async function handleGenerateConsistencyAssetReference(type: ConsistencyAssetType, assetId: string) {
    const asset = consistencyAssets.value[type].find((item) => item.id === assetId)
    if (!asset) {
      return
    }

    const persistedAsset = persistedConsistencyAssets.value[type].find((item) => item.id === assetId)
    if (consistencyDirty.value || !persistedAsset) {
      const saved = await handleSaveConsistencyAssets('一致性资产已保存，开始生成参考图')
      if (!saved) {
        return
      }
    }

    const loadingKey = buildConsistencyAssetLoadingKey(type, assetId)
    consistencyAssetImageGenerating.value = { ...consistencyAssetImageGenerating.value, [loadingKey]: true }
    options.workspaceBusyState.value = {
      title: `AI 正在生成${CONSISTENCY_ASSET_LABELS[type]}参考图`,
      description: `系统正在根据你确认的${CONSISTENCY_ASSET_LABELS[type]}描述生成统一参考图，完成后会自动回填到一致性资产卡片。`,
      stageLabel: '分镜制作',
      notice: '一致性资产参考图生成完成后，后续分镜图片和视频描述会优先吸收这些官方参考。',
    }
    try {
      const response = await options.generateConsistencyAssetImageApi(options.projectId, type, assetId, {
        prompt: buildConsistencyAssetImagePrompt(asset),
      })
      if (response.asset) {
        updateConsistencyAsset(type, assetId, (current) => ({
          ...current,
          name: type === 'scene'
            ? normalizeSceneAssetName(response.asset.name || current.name || '')
            : String(response.asset.name || current.name || ''),
          prompt: collapseConsistencyAssetText(response.asset.prompt || current.prompt || '', response.asset.notes || current.notes || ''),
          notes: '',
          image_url: String(response.asset.image_url || current.image_url || ''),
          material_id: Number(response.asset.material_id || current.material_id || 0) || undefined,
          selected_image_source: String(response.asset.selected_image_source || current.selected_image_source || ''),
          generated_prompt: String(response.asset.generated_prompt || current.generated_prompt || ''),
        }))
      }
      options.workflow.value = response.workflow
      syncConsistencyAssetsFromStoryboard()
      consistencyDirty.value = false
      options.success(`${CONSISTENCY_ASSET_LABELS[type]}参考图已生成`)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || '一致性资产参考图生成失败')
    } finally {
      consistencyAssetImageGenerating.value = { ...consistencyAssetImageGenerating.value, [loadingKey]: false }
      options.workspaceBusyState.value = null
    }
  }

  function handleClearConsistencyAssetImage(type: ConsistencyAssetType, assetId: string) {
    updateConsistencyAsset(type, assetId, (current) => ({
      ...current,
      image_url: '',
      material_id: undefined,
      selected_image_source: '',
      generated_prompt: '',
    }))
  }

  return {
    activeConsistencyAssets,
    consistencyAssetImageGenerating,
    consistencyAssetImageUploading,
    consistencyAssetLookups,
    consistencyAssetSelectionOptions,
    consistencyAssetStats,
    consistencyAssetUploadInput,
    consistencyAssets,
    consistencyDirty,
    consistencySaving,
    handleAddConsistencyAsset,
    handleCreatePromptMentionAsset,
    handleClearConsistencyAssetImage,
    handleConsistencyAssetFileSelected,
    handleGenerateConsistencyAssetReference,
    handleRemoveConsistencyAsset,
    handleSaveConsistencyAssets,
    markConsistencyDirty,
    selectedStoryboardPromptMentionItems,
    syncConsistencyAssetsFromStoryboard,
    triggerConsistencyAssetUpload,
  }
}